import { useRouter } from 'next/router';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

import useLocalStorageState from 'hooks/useLocalStorageState';

import { getFromLocalStorage } from 'lib/localStorage';
import { parseToBool } from 'lib/string';
import { noop } from 'lib/utils';

import LOCAL_STORAGE from 'constants/localStorage';

const PAGES_FORCED_DARK_THEME = ['/black-friday', '/minha-black-friday'];
const OPTIONS = {
  light: 'light',
  dark: 'dark',
};

const applyTheme = (theme) => {
  document.documentElement.classList = '';
  document.documentElement.classList.add(theme);
};

const ThemeContext = createContext({
  options: null,
  theme: null,
  isAllowedToChangeTheme: true,
  isDarkThemeActive: false,
  setTheme: noop,
});

const ThemeProvider = ({ children }) => {
  const router = useRouter();
  const [isAllowedToChangeTheme, setAllowedToChangeTheme] = useState(false);
  const [isPreferredDarkTheme, setPreferredDarkTheme] = useLocalStorageState(
    LOCAL_STORAGE.IS_DARK_MODE_ACTIVE,
    false
  );

  const setTheme = useCallback((theme) => {
    applyTheme(theme);
    setPreferredDarkTheme(theme === OPTIONS.dark);
  }, []);

  useEffect(() => {
    const forceTheme = () => {
      const isPageThatNeedsDarkTheme = PAGES_FORCED_DARK_THEME.some((path) =>
        router.pathname.includes(path)
      );
      const isDarkThemePreferred = parseToBool(
        getFromLocalStorage(LOCAL_STORAGE.IS_DARK_MODE_ACTIVE)
      );

      setAllowedToChangeTheme(!isPageThatNeedsDarkTheme);

      if (isPageThatNeedsDarkTheme || isDarkThemePreferred) {
        applyTheme(OPTIONS.dark);
        return;
      }

      applyTheme(OPTIONS.light);
    };

    forceTheme();

    router.events.on('routeChangeComplete', forceTheme);

    return () => {
      router.events.off('routeChangeComplete', forceTheme);
    };
  }, [router]);

  const value = useMemo(
    () => ({
      isAllowedToChangeTheme,
      options: OPTIONS,
      theme: isPreferredDarkTheme ? OPTIONS.dark : OPTIONS.light,
      isDarkThemeActive: isPreferredDarkTheme,
      setTheme,
    }),
    [isAllowedToChangeTheme, isPreferredDarkTheme]
  );

  return (
    <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
  );
};

export const useTheme = () => useContext(ThemeContext);

export default ThemeProvider;
